import type { ApiUserAccount } from 'src/types/userAuth';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { SESSION_STALE_TIME } from './staleness';

/**
 * GET /user/auth/me — the account behind the browser's session cookie (api#454), or 401 for a
 * visitor who holds none. Asked once the browser has the page (`epoch` is `null` on the server and
 * for the render that adopts its markup), and keyed by the tab's epoch: a sign-in or a sign-out is
 * a new query, so a key and an answer are never from two different sessions.
 *
 * Re-asked whenever the tab comes back into focus, whatever its age: the storefront and the dealer
 * cabinet hold one session, and a sign-out in the cabinet is learnt here at the next look — this is
 * also the heartbeat that keeps the session alive (the api moves its expiry on and re-sends the
 * cookie). It costs nothing an anonymous visitor would notice: a refused `/me` is one small answer.
 *
 * Retries follow the app-wide rule (`shouldRetryRequest` in `root.tsx`): a 4xx is a verdict and is
 * not re-asked, a 5xx or a dropped connection is asked once more. The `retry: false` this query
 * used to carry turned every gateway hiccup into a session-ending error on the first try (app#207).
 */
export function useUserMe(
  epoch: number | null,
  { heartbeat = true }: { heartbeat?: boolean } = {}
) {
  return useQuery({
    enabled: epoch !== null,
    queryKey: QueryKeys.userMeFor(epoch ?? 0),
    queryFn: async (): Promise<ApiUserAccount> => {
      const response = await axios.get<ApiUserAccount>(`/user/auth/me`);
      return response.data;
    },
    staleTime: SESSION_STALE_TIME,
    // The heartbeat is for a browser that holds, or held, a session; a guest's tab switches ask nothing.
    refetchOnWindowFocus: heartbeat ? 'always' : false,
  });
}
